import React, { useState } from 'react';
import axios from 'axios';
import PhoneInput, { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import en from 'react-phone-number-input/locale/en';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Mail, Phone, User, Building } from 'lucide-react';
import { toast } from 'sonner';
import { AIRTABLE_API_KEY, AIRTABLE_BASE_ID, AIRTABLE_TABLE_NAME } from './config';

const api_key = AIRTABLE_API_KEY;
const base_id = AIRTABLE_BASE_ID;
const table_name = AIRTABLE_TABLE_NAME;

// Generate country labels with calling codes, e.g., "India +91"
const countryLabels = {};
getCountries().forEach((country) => {
  countryLabels[country] = `${en[country]} (+${getCountryCallingCode(country)})`;
});


export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
      toast('Invalid Phone Number', {
        description: 'Please enter a valid phone number including the country code.',
        // variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    const pageUrl = window.location.href || document.location.href;

    try {
      const response = await axios.post(
        `https://api.airtable.com/v0/${base_id}/${table_name}`,
        {
          records: [
            {
              fields: {
                Name: formData.name,
                Email: formData.email,
                Organization: formData.company,
                Phone: formData.phone,
                Question: formData.message,
                PageUrl: pageUrl,
              },
            },
          ],
          typecast: true,
        },
        {
          headers: {
            Authorization: `Bearer ${api_key}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // console.log('Airtable Response:', response.data);

      toast('Thank you!', {
        description: 'Your message has been received. We will be in touch shortly.',
      });

      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting to Airtable:', error);
      if (error.response) {
        console.error('Airtable Error Details:', error.response.data);
      }
      toast('Submission Failed', {
        description: 'There was an error sending your message. Please try again.',
        // variant: 'destructive', // Sonner doesn't use variant in this way, but we can leave it or use toast.error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Get Started Today
          </h2>
          <p className="text-lg text-gray-600">
            Let's discuss how we can help with your Windchill needs
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl">Contact Us</CardTitle>
            <CardDescription>
              Fill out the form below and our expert team will reach out to you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center space-x-2">
                    <User size={16} />
                    <span>Full Name *</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="focus:ring-[#00890b] focus:border-[#00890b]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center space-x-2">
                    <Mail size={16} />
                    <span>Email *</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                    className="focus:ring-[#00890b] focus:border-[#00890b]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="flex items-center space-x-2">
                    <Building size={16} />
                    <span>Company</span>
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    className="focus:ring-[#00890b] focus:border-[#00890b]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center space-x-2">
                    <Phone size={16} />
                    <span>Phone</span>
                  </Label>
                  <div className="phone-input-container">
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="IN"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="Enter phone number"
                      labels={countryLabels}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-within:ring-2 focus-within:ring-[#00890b] focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your Windchill requirements..."
                  rows={5}
                  required
                  className="focus:ring-[#00890b] focus:border-[#00890b]"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00890b] hover:bg-[#006a08] text-white text-lg py-6"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};